/**
 * Manuscript Parser Utility
 * 
 * Handles parsing of manuscript filenames according to the format:
 * [Chapter Abbreviation] [Manuscript Siglum].ext
 * 
 * Example: "Lo Paris 3465.txt" -> { chapter: "Lo", siglum: "Paris 3465" }
 */

// Hardcoded list of valid chapter abbreviations
export const VALID_CHAPTERS = [
  'Incipit', 'ToC', 'As', 'Lv', 'Sv', 'Im', 'Bu', 'Lo', 'Di', 'Rd',
  'Oc', 'Mc', 'Kd', 'Lj', 'Kb', 'At', 'Aw', 'Lh', 'Ag', 'Tg',
  'Ks', 'Df', 'Kw', 'Km'
] as const

export type ChapterAbbreviation = typeof VALID_CHAPTERS[number]

// Hardcoded list of valid manuscript sigla (without parenthetical notes)
export const VALID_SIGLA = [
  'Vienna AF 298',
  'ÖNB AF 89',
  'Montreal 117953',
  'Dar al-Kutub 1169',
  'Azhariyya 66947',
  'Azhariyya 53544',
  'Paris 2789',
  'Paris 3465',
  'Paris 3466',
  'Paris 3467',
  'Paris 3468',
  'Paris 3469',
  'Paris 3470',
  'Paris 3471',
  'Paris 3472',
  'Paris 3473',
  'Paris 3474',
  'Paris 3475',
  'Paris 3476',
  'Paris 3477',
  'Paris 3478',
  'Paris 3479',
  'Paris 3480',
  'Paris 3593',
  'Paris 5881',
  'Schiller',
  'Strasbourg 4217',
  'Sprenger 1246',
  'Wetzstein II 672',
  'Gotha A2691',
  'München 615',
  'München 616',
  'München 617',
  'München 618',
  'München 619',
  'Tübingen MaVI35',
  'Hamburg 170',
  'Cairo Geniza T-S Ar. 6.321',
  'Cairo Geniza T-S Ar. 40.9',
  'Cairo Geniza T-S Ar. 51.60',
  'Cairo Geniza T-S NS 97.15',
  'Cambridge Parker 578',
  'Clarke Or. 9',
  'London 3900',
  'London 4044',
  'London 4593',
  'London 8751',
  'London Add. 23466',
  'London Add. 24350',
  'London Add. 7413',
  'London Add. 7414',
  'Manchester 2',
  'Manchester 60',
  'Manchester 537',
  'Bodleian 253',
  'Pococke 400',
  'Bodleian March 673',
  'Malek 1669',
  'Majlis-i Shūrā 16288',
  'Mosul 283',
  'Dublin 4201',
  'Saint Anthony LP018',
  'Beirut 0022',
  'Beirut 01875',
  'Beirut 00360',
  'Sarba 989',
  'Ibn al-Habbariya',
  'Cheikho',
  'Daoudia 285',
  'Rabat 3655',
  'Rabat 12156',
  'Leiden Or 1544',
  'Leiden Or 1590',
  'QNL HC. MS. 2018.0016',
  'Riyadh 1850',
  'Riyadh 2407',
  'Riyadh 2536',
  'Riyadh 5768',
  'Riyadh 6146',
  'Riyadh 7053',
  'Riyadh 12965',
  'Umm Qura Muḍāhāt',
  'Aleppo 143',
  'Aleppo 406',
  'Tunis 2281',
  'Tunis 4459',
  'Tunis 8623',
  'Tunis 9760',
  'Tunis 16029',
  'Arch. Museum EY 344',
  'Ayasofya 4095',
  'Ayasofya 4213',
  'Ayasofya 4214',
  'Laleli 1793',
  'Mardin 491',
  'Süleymaniye Kütüphanesi 03542-006',
  'Sultan Ahmed',
  'Sultan Ahmed III 3015',
  'Chicago A 12101',
  'Chicago A 11991',
  'Met 1981.373',
  'Princeton 17L',
  'Princeton 169H',
  'Yale A 270',
  'Biruni 3629',
  'Vatican Sbath 267',
  'Vatican Borg. 367',
  'Vatican Borg. 213'
] as const

export type ManuscriptSiglum = typeof VALID_SIGLA[number]

const SIGLUM_ALIASES: Array<{ siglum: ManuscriptSiglum; aliases: readonly string[] }> = [
  {
    siglum: 'Cambridge Parker 578',
    aliases: [
      'Parker 578',
      'Corpus Christi Parker 578',
      'Corpus Christi College Parker 578',
      'CCCC 578',
    ],
  },
]

export interface ParsedManuscript {
  chapter: ChapterAbbreviation
  siglum: ManuscriptSiglum
  filename: string
  isValid: boolean
  error?: string
}

export interface ManualInputData {
  chapter: string
  siglum: string
}

/**
 * Parse a filename to extract chapter abbreviation and manuscript siglum
 * Uses flexible fuzzy matching to find chapter and siglum anywhere in the filename
 * 
 * Examples:
 * - "Lo Paris 3465.txt" → { chapter: "Lo", siglum: "Paris 3465" }
 * - "AsVienna AF 298.docx" → { chapter: "As", siglum: "Vienna AF 298" }
 * - "Paris_3465_Lo_manuscript.rtf" → { chapter: "Lo", siglum: "Paris 3465" }
 * - "As Sultan Ahmed ent. OEK KK HAH 06.08.2024.rtf" → { chapter: "As", siglum: "Sultan Ahmed" }
 */
export function parseManuscriptFilename(filename: string): ParsedManuscript {
  console.log(`🔍 PARSING FILENAME: "${filename}"`);
  
  // Remove file extension
  const nameWithoutExt = filename.substring(0, filename.lastIndexOf('.')) || filename
  console.log(`📝 Name without extension: "${nameWithoutExt}"`);
  
  // Try fuzzy matching approach
  const fuzzyMatch = tryFuzzyMatching(nameWithoutExt, filename)
  console.log(`🎯 Fuzzy match result:`, fuzzyMatch);
  
  if (fuzzyMatch.isValid) {
    console.log(`✅ PARSING SUCCESS: Chapter="${fuzzyMatch.chapter}", Siglum="${fuzzyMatch.siglum}"`);
    return fuzzyMatch
  }
  
  // If fuzzy matching fails, return what we found with error
  const result = {
    chapter: fuzzyMatch.chapter || '' as ChapterAbbreviation,
    siglum: fuzzyMatch.siglum || '' as ManuscriptSiglum,
    filename,
    isValid: false,
    error: `Could not find both chapter and siglum in filename "${filename}". ${fuzzyMatch.chapter ? `Found chapter: ${fuzzyMatch.chapter}` : 'No valid chapter found'}. ${fuzzyMatch.siglum ? `Found siglum: ${fuzzyMatch.siglum}` : 'No valid siglum found'}. ${getSuggestions(nameWithoutExt)}`
  }
  console.log(`❌ PARSING FAILED:`, result);
  return result;
}

/**
 * Try fuzzy matching to find chapter and siglum anywhere in the filename
 * This is the main parsing strategy that looks for valid chapters and sigla
 * regardless of their position in the filename
 */
function tryFuzzyMatching(nameWithoutExt: string, filename: string): ParsedManuscript {
  let foundChapter: ChapterAbbreviation | null = null
  let foundSiglum: ManuscriptSiglum | null = null
  
  // Step 1: Find chapter anywhere in the filename
  foundChapter = findChapterInFilename(nameWithoutExt)
  console.log(`📖 Found chapter: ${foundChapter || 'None'}`);
  
  // Step 2: Find siglum anywhere in the filename
  foundSiglum = findSiglumInFilename(nameWithoutExt)
  console.log(`📜 Found siglum: ${foundSiglum || 'None'}`);
  
  // Step 3: If we found both, return success
  if (foundChapter && foundSiglum) {
    console.log(`🎉 FUZZY MATCH SUCCESS!`);
    return {
      chapter: foundChapter,
      siglum: foundSiglum,
      filename,
      isValid: true
    }
  }
  
  // Step 4: Return partial match with error info
  console.log(`⚠️ FUZZY MATCH PARTIAL: Chapter=${foundChapter}, Siglum=${foundSiglum}`);
  return {
    chapter: foundChapter || '' as ChapterAbbreviation,
    siglum: foundSiglum || '' as ManuscriptSiglum,
    filename,
    isValid: false,
    error: `Incomplete match: ${foundChapter ? `Found chapter "${foundChapter}"` : 'No chapter found'}, ${foundSiglum ? `Found siglum "${foundSiglum}"` : 'No siglum found'}`
  }
}

/**
 * Find chapter abbreviation anywhere in the filename
 * Uses multiple strategies to locate a valid chapter
 */
function findChapterInFilename(nameWithoutExt: string): ChapterAbbreviation | null {
  // Normalize Unicode characters to handle composed vs decomposed characters
  const normalized = nameWithoutExt.normalize('NFC').toLowerCase().replace(/[_.-]/g, ' ').replace(/\s+/g, ' ').trim()
  
  // Strategy 1: Look for exact chapter matches (case-insensitive)
  for (const chapter of VALID_CHAPTERS) {
    const chapterLower = chapter.toLowerCase()
    
    // Check if chapter appears as a word boundary
    const regex = new RegExp(`\\b${chapterLower}\\b`, 'i')
    if (regex.test(normalized)) {
      return chapter
    }
    
    // Check if chapter appears at the beginning
    if (normalized.startsWith(chapterLower + ' ') || normalized.startsWith(chapterLower)) {
      return chapter
    }
    
    // Check if chapter appears with common separators
    const patterns = [
      `${chapterLower}_`,
      `${chapterLower}-`,
      `${chapterLower}.`,
      `_${chapterLower}_`,
      `-${chapterLower}-`,
      `.${chapterLower}.`
    ]
    
    for (const pattern of patterns) {
      if (normalized.includes(pattern)) {
        return chapter
      }
    }
  }
  
  return null
}

const tokeniseSiglumMatchText = (value: string): string[] => (
  value
    .normalize('NFC')
    .replace(/[_.()[\]{}-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
    .split(' ')
    .filter(Boolean)
)

const containsTokenSequence = (haystackTokens: string[], needleTokens: string[]): boolean => {
  if (needleTokens.length === 0 || needleTokens.length > haystackTokens.length) {
    return false
  }

  return haystackTokens.some((_, startIndex) => (
    needleTokens.every((needleToken, offset) => haystackTokens[startIndex + offset] === needleToken)
  ))
}

function findSiglumAliasInFilename(nameWithoutExt: string): ManuscriptSiglum | null {
  const filenameTokens = tokeniseSiglumMatchText(nameWithoutExt)

  for (const { siglum, aliases } of SIGLUM_ALIASES) {
    for (const alias of aliases) {
      if (containsTokenSequence(filenameTokens, tokeniseSiglumMatchText(alias))) {
        return siglum
      }
    }
  }

  return null
}

/**
 * Find manuscript siglum anywhere in the filename using fuzzy matching
 * Uses multiple strategies to locate a valid siglum
 */
function findSiglumInFilename(nameWithoutExt: string): ManuscriptSiglum | null {
  console.log(`🔍 Looking for siglum in: "${nameWithoutExt}"`)
  // Normalize Unicode characters to handle composed vs decomposed umlauts
  const unicodeNormalized = nameWithoutExt.normalize('NFC')
  const normalized = unicodeNormalized.replace(/[_.-]/g, ' ').replace(/\s+/g, ' ').trim()
  console.log(`📝 Normalized: "${normalized}"`)

  const aliasMatch = findSiglumAliasInFilename(nameWithoutExt)
  if (aliasMatch) {
    console.log(`✅ Found "${aliasMatch}" with curated alias match`)
    return aliasMatch
  }
  
  // Additional debug for München case
  if (nameWithoutExt.includes('München') || unicodeNormalized.includes('München')) {
    console.log(`🔍 MÜNCHEN FILENAME DETECTED!`)
    console.log(`   Original: "${nameWithoutExt}"`)
    console.log(`   Unicode normalized: "${unicodeNormalized}"`)
    console.log(`   Final normalized: "${normalized}"`)
    console.log(`   Contains "münchen 618"? ${normalized.toLowerCase().includes('münchen 618')}`)
  }
  
  // Strategy 1: Direct exact matching
  console.log(`🎯 Strategy 1: Direct exact matching`)
  for (const siglum of VALID_SIGLA) {
    if (normalized.includes(siglum)) {
      console.log(`✅ Found "${siglum}" with direct match`)
      return siglum
    }
    // Debug München specifically
    if (siglum.includes('München')) {
      console.log(`   Checking München: "${siglum}" in "${normalized}" = ${normalized.includes(siglum)}`)
    }
  }
  console.log(`❌ Strategy 1 failed - proceeding to Strategy 2`)
  
  // Strategy 2: Case-insensitive matching
  console.log(`🎯 Strategy 2: Case-insensitive matching`)
  const normalizedLower = normalized.toLowerCase()
  console.log(`📝 Normalized lower: "${normalizedLower}"`)
  
  // Debug: Check if the normalization worked
  console.log(`🔍 Unicode check: Character 4 is "${normalizedLower[4]}" (${normalizedLower.charCodeAt(4)})`)
  
  for (const siglum of VALID_SIGLA) {
    // Also normalize the siglum for comparison
    const siglumNormalized = siglum.normalize('NFC').toLowerCase()
    const isMatch = normalizedLower.includes(siglumNormalized)
    if (isMatch) {
      console.log(`✅ Found "${siglum}" with case-insensitive match`)
      return siglum
    }
    // Debug München specifically
    if (siglum.includes('München')) {
      console.log(`   🔍 MÜNCHEN DEBUG: "${siglumNormalized}" in "${normalizedLower}" = ${normalizedLower.includes(siglumNormalized)}`)
    }
  }
  
  // Strategy 3: Remove spaces and match
  const normalizedNoSpaces = normalized.replace(/\s/g, '')
  for (const siglum of VALID_SIGLA) {
    const siglumNoSpaces = siglum.replace(/\s/g, '')
    if (normalizedNoSpaces.toLowerCase().includes(siglumNoSpaces.toLowerCase())) {
      return siglum
    }
  }
  
  // Strategy 4: Partial matching for complex sigla
  // Look for significant parts of the siglum
  for (const siglum of VALID_SIGLA) {
    const siglumParts = siglum.split(' ')
    if (siglumParts.length >= 2) {
      // Check if all parts of the siglum are present
      const allPartsFound = siglumParts.every(part => {
        if (part.length >= 2) { // Only check meaningful parts
          return normalizedLower.includes(part.toLowerCase())
        }
        return true
      })
      
      if (allPartsFound) {
        return siglum
      }
    }
  }
  
  // Strategy 5: Handle special cases for numbers
  // e.g., "Paris3465" should match "Paris 3465"
  for (const siglum of VALID_SIGLA) {
    const siglumCompact = siglum.replace(/\s+/g, '')
    if (normalizedNoSpaces.toLowerCase().includes(siglumCompact.toLowerCase())) {
      return siglum
    }
  }
  
  // Strategy 6: Handle umlaut variations
  // e.g., "Munchen 618" should match "München 618"
  const normalizedWithUmlautSubstitutions = normalizedLower
    .replace(/ue/g, 'ü')
    .replace(/ae/g, 'ä')
    .replace(/oe/g, 'ö')
    .replace(/ss/g, 'ß')
  
  for (const siglum of VALID_SIGLA) {
    const siglumLower = siglum.toLowerCase()
    if (normalizedWithUmlautSubstitutions.includes(siglumLower)) {
      return siglum
    }
  }
  
  // Strategy 7: Handle reverse umlaut variations  
  // e.g., "München 618" should match "Munchen 618" input
  const normalizedWithoutUmlauts = normalizedLower
    .replace(/ü/g, 'u')
    .replace(/ä/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/ß/g, 'ss')
  
  for (const siglum of VALID_SIGLA) {
    const siglumWithoutUmlauts = siglum.toLowerCase()
      .replace(/ü/g, 'u')
      .replace(/ä/g, 'a')
      .replace(/ö/g, 'o')
      .replace(/ß/g, 'ss')
    
    if (normalizedWithoutUmlauts.includes(siglumWithoutUmlauts)) {
      return siglum
    }
  }
  
  // Strategy 8: Handle umlaut variations with space removal
  // e.g., "BuMuenchen618" should match "München 618"
  const normalizedNoSpacesWithUmlauts = normalizedNoSpaces.toLowerCase()
    .replace(/ue/g, 'ü')
    .replace(/ae/g, 'ä')
    .replace(/oe/g, 'ö')
    .replace(/ss/g, 'ß')
  
  for (const siglum of VALID_SIGLA) {
    const siglumNoSpaces = siglum.replace(/\s/g, '').toLowerCase()
    if (normalizedNoSpacesWithUmlauts.includes(siglumNoSpaces)) {
      return siglum
    }
  }
  
  // Strategy 9: Handle space removal with umlaut normalization
  // e.g., "BuMunchen618" should match "München 618"
  const normalizedNoSpacesNoUmlauts = normalizedNoSpaces.toLowerCase()
    .replace(/ü/g, 'u')
    .replace(/ä/g, 'a')
    .replace(/ö/g, 'o')
    .replace(/ß/g, 'ss')
  
  for (const siglum of VALID_SIGLA) {
    const siglumNoSpacesNoUmlauts = siglum.replace(/\s/g, '').toLowerCase()
      .replace(/ü/g, 'u')
      .replace(/ä/g, 'a')
      .replace(/ö/g, 'o')
      .replace(/ß/g, 'ss')
    
    if (normalizedNoSpacesNoUmlauts.includes(siglumNoSpacesNoUmlauts)) {
      return siglum
    }
  }
  
  return null
}

/**
 * Generate helpful suggestions for common issues
 */
function getSuggestions(nameWithoutExt: string): string {
  const suggestions: string[] = []
  
  // Check if it might be missing a space
  for (const chapter of VALID_CHAPTERS) {
    if (nameWithoutExt.toLowerCase().startsWith(chapter.toLowerCase()) && 
        !nameWithoutExt.startsWith(chapter + ' ')) {
      suggestions.push(`Try adding a space after "${chapter}": "${chapter} ${nameWithoutExt.substring(chapter.length)}"`);
      break;
    }
  }
  
  // Check for common separators
  if (nameWithoutExt.includes('_') || nameWithoutExt.includes('-')) {
    suggestions.push('Try replacing underscores/hyphens with spaces');
  }
  
  // Suggest some common valid examples
  if (suggestions.length === 0) {
    suggestions.push('Valid examples: "Lo Paris 3465.txt", "As Vienna AF 298.docx"');
  }
  
  return suggestions.length > 0 ? `Suggestions: ${suggestions.join('; ')}` : '';
}

/**
 * Validate manual input for chapter and siglum
 */
export function validateManualInput(data: ManualInputData): ParsedManuscript {
  const chapter = VALID_CHAPTERS.find(ch => ch === data.chapter)
  const siglum = VALID_SIGLA.find(sig => sig === data.siglum)
  
  if (!chapter) {
    return {
      chapter: '' as ChapterAbbreviation,
      siglum: '' as ManuscriptSiglum,
      filename: '',
      isValid: false,
      error: `Invalid chapter "${data.chapter}". Expected one of: ${VALID_CHAPTERS.join(', ')}`
    }
  }
  
  if (!siglum) {
    return {
      chapter: chapter,
      siglum: '' as ManuscriptSiglum,
      filename: '',
      isValid: false,
      error: `Invalid siglum "${data.siglum}"`
    }
  }
  
  return {
    chapter,
    siglum,
    filename: `${chapter} ${siglum}`,
    isValid: true
  }
}

/**
 * Get full chapter name from abbreviation
 */
export function getFullChapterName(abbreviation: ChapterAbbreviation): string {
  // This could be expanded with full names if needed
  return abbreviation
}

/**
 * Format a parsed manuscript for display
 */
export function formatManuscriptDisplay(parsed: ParsedManuscript): string {
  if (!parsed.isValid) {
    return parsed.filename
  }
  return `${parsed.chapter} - ${parsed.siglum}`
}
